import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1098FInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1098-F instructions for Tax Year 2025"
          description="Learn how to fill out IRS Form 1098-F for Tax Year 2025 with step-by-step guidance from TaxZerone to accurately report exempt organization donations."
          keywords=""
          canonical="resources/information-returns/form-1098-f-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1098-F</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report fines with Ease and File on Time!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098finstructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1098-f-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1098-F at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Form 1098-F</strong>
                        {
                          ' reports certain court-ordered fines, penalties, and other amounts paid to governments or governmental entities. Governmental entities use this form to report amounts that may qualify as deductible restitution, remediation, or amounts paid to come into compliance with the law under IRS reporting requirements.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Payer copies
                        </strong>
                        {' must be furnished by '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>
                        {
                          ' following the calendar year in which the reportable amount is paid. The '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          IRS filing deadline
                        </strong>
                        {' is '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {'This article covers '}
                        <strong style={{ fontWeight: 600 }}>
                          line-by-line instructions
                        </strong>
                        {', including filer and payer details, '}
                        <strong style={{ fontWeight: 600 }}>{'Box 1 '}</strong>
                        {'(Total Amount Required to Be Paid),'}{' '}
                        <strong style={{ fontWeight: 600 }}>{'Box 2 '}</strong>
                        {
                          '(Amount to Be Paid for Violation or Potential Violation of Law),'
                        }{' '}
                        <strong style={{ fontWeight: 600 }}>{'Box 3 '}</strong>
                        (Restitution, Remediation, or Required to Come into
                        Compliance with Law)
                        {','}{' '}
                        <strong style={{ fontWeight: 600 }}>{'Box 4 '}</strong>
                        (Compliance Amount)
                        <strong style={{ fontWeight: 600 }}>
                          {', Box 5 '}
                        </strong>
                        (Date the Suit, Order, or Agreement Became Binding)
                        <strong style={{ fontWeight: 600 }}>, Box 6</strong>
                        {' (Court or Entity Information),'}
                        <strong style={{ fontWeight: 600 }}>{' Box 7 '}</strong>
                        (Case Number),
                        <strong style={{ fontWeight: 600 }}>{' Box 8'}</strong>
                        {
                          ' (Case Name or Names of Parties to the Suit, Order, or Agreement),'
                        }
                        <strong style={{ fontWeight: 600 }}>{' and '}</strong>
                        <strong style={{ fontWeight: 600 }}>Box 9</strong>
                        {' (Code)'}.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {' Late, incorrect, or incomplete filing of '}
                        <strong style={{ fontWeight: 600 }}>Form 1098-F</strong>
                        {' may result in IRS penalties ranging from '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on the delay, correction requirements, and
                        whether the filer meets electronic filing obligations.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies.'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  IRS Form 1098-F, is used to report fines, penalties, or other
                  amounts paid by individuals, businesses, or other entities as
                  a result of violating laws, regulations, or environmental
                  rules. This form is intended to help ensure that these
                  payments are reported accurately to the Internal Revenue
                  Service (IRS).
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  {' '}
                  Government agencies or entities (including nongovernmental
                  entities treated as governmental) that assess or collect these
                  fines or penalties are responsible for filing this form.
                  Additionally, a copy of Form 1098-F must be sent to the
                  recipient, the individual or entity that incurred the fine or
                  penalty.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who required to file for 1098-F?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#dead" sx={{ color: '#0202ff' }}>
                          Important deadline to file for form 1098-F
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form" sx={{ color: '#0202ff' }}>
                          Form 1098-F instructions-how to fill out?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who required to file form 1098-F?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The government or governmental entity (including
                  nongovernmental entities treated as governmental entities,)
                  must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-f/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1098-F
                  </Link>{' '}
                  to report any fines, penalties, or other amounts. These
                  payments generally arise from violations of laws, regulations,
                  or environmental rules imposed by federal, state, or local
                  authorities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you are filing Form 1098-F, you must provide a statement to
                  the payer. Filers of Form 1098-F may truncate a payer&apos;s
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Taxpayer Identification Number
                  </span>{' '}
                  (TIN) on statements furnished to the payer, but not on IRS
                  filings. A filer&apos;s own TIN{' '}
                  <span style={{ fontWeight: 600 }}>cannot be truncated</span>
                  .{' '}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="dead"
                >
                  Important deadline to file for form 1098-F:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The{' '}
                  <Link
                    href="/resources/information-returns/form-1098-f-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    form 1098-f deadline
                  </Link>{' '}
                  are given below for the tax year:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  To IRS:
                </Typography>
                <Box component="ol" sx={{ mb: 3.5, '& li': { mb: 1.5 } }}>
                  <li>
                    <b> E-Filing:</b> The IRS recommends electronic filing for
                    Form 1098-F and requires it for entities submitting 10 or
                    more information returns.
                    <br />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 31, 2026
                      </Typography>
                    </Box>
                  </li>
                  <li>
                    <b> Paper filing:</b> Entities can use the paper filing
                    method if they are submitting fewer than 10 forms.
                    <br />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 2, 2026
                      </Typography>
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  To the Payer:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  The appropriate entity must provide a copy of Form 1098-F to
                  the recipient (the person or entity that incurred the fine or
                  penalty) by{' '}
                  <span style={{ fontWeight: 600 }}> January 31</span> of the
                  following year.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If the deadline falls on the federal holiday, it
                  will extend to the next business day. Filing before the
                  deadline to avoid late penalties.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 4,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="form"
                >
                  Form 1098-F instructions-how to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Step-by-step instructions to file form 1098-F
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 1: Enter the Filer’s details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-filer-details-img.webp" />
                  <source srcSet="/assets/images/f1098-filer-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-filer-details-img.png"
                    loading="lazy"
                    alt="Step 1: Enter the Filer’s details"
                    width={525}
                    height={157}
                    className="img-responsive"
                  />
                </picture>
                <Box sx={{ mb: 2 }}>
                  <picture>
                    <source srcSet="/assets/images/f1098-f-filer-img.webp" />
                    <source srcSet="/assets/images/f1098-f-filer-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f1098-f-filer-img.png"
                      loading="lazy"
                      alt="Step 1: Enter the Filer’s details"
                      width={264}
                      height={54}
                      className="img-responsive"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the filer’s name, address and telephone number along
                  with Filer’s Taxpayer Identification Number. Use the same name
                  and address on Form 1096.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Use the same name and address on Form 1096.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 2: Enter the Payer’s details
                </Typography>{' '}
                <picture>
                  <source srcSet="/assets/images/f1098-f-payers-img.webp" />
                  <source srcSet="/assets/images/f1098-f-payers-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-f-payers-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the Payer’s details"
                    width={264}
                    height={54}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Payer’s Taxpayer Identification Number (TIN).
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-f-payer-details-img.webp" />
                  <source srcSet="/assets/images/f1098-f-payer-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-f-payer-details-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the Payer’s details"
                    width={525}
                    height={183}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Payer’s name, and address including apartment
                  number, city, state, country, and ZIP code.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 3: Fines, Penalties and other amounts details
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Total Amount to be paid Pursuant to the Suit, Order, or
                  Agreement
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-f-total-amount-img.webp" />
                  <source srcSet="/assets/images/f1098-f-total-amount-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-f-total-amount-img.png"
                    loading="lazy"
                    alt="Box 1: Total Amount to be paid Pursuant to the Suit, Order, or Agreement"
                    width={214}
                    height={54}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total amount due to suit, order or agreement which
                  the amount equals or exceeds $50,000.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Amount To Be Paid for Violation or Potential Violation
                </Typography>
                <Box>
                  <picture>
                    <source srcSet="/assets/images/f1098-f-potential-img.webp" />
                    <source srcSet="/assets/images/f1098-f-potential-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f1098-f-potential-img.png"
                      loading="lazy"
                      alt="Box 2: Amount To Be Paid for Violation or Potential Violation"
                      width={217}
                      height={66}
                      className="img-responsive"
                    />
                  </picture>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount required to be paid for a violation,
                  investigation, or inquiry into a potential legal violation. If
                  some or all of the amounts paid to investigation and potential
                  legal violation of law is not identified. Leave Blank on Box 2
                  and write Code E in Box 9.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3. Restitution/Remediation Amount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-f-restitution-img.webp" />
                  <source srcSet="/assets/images/f1098-f-restitution-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-f-restitution-img.png"
                    loading="lazy"
                    alt="Box 3. Restitution/Remediation Amount"
                    width={217}
                    height={66}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount required to be paid for restitution/
                  remediation under the suit, order or agreement. If the some or
                  all of the amounts paid for restitution/ remediation is not
                  identified. Leave Blank on Box 3 and write code E in Box 9.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4. Compliance Amount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-f-compliance-img.webp" />
                  <source srcSet="/assets/images/f1098-f-compliance-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-f-compliance-img.png"
                    loading="lazy"
                    alt="Box 4. Compliance Amount"
                    width={217}
                    height={53}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount required to be paid as result of suit, order
                  or agreement, for the purpose of compliance with a law. If
                  some or all of the amounts paid is not identified. Leave the
                  blank in Box 4 and write the code E in Box 9.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5. Date of Order/Agreement
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-f-agreement-img.webp" />
                  <source srcSet="/assets/images/f1098-f-agreement-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-f-agreement-img.png"
                    loading="lazy"
                    alt="Box 5. Date of Order/Agreement"
                    width={217}
                    height={79}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the date when the court issued the order or when the
                  agreement was fully executed.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6. Court or Entity
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-f-court-img.webp" />
                  <source srcSet="/assets/images/f1098-f-court-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-f-court-img.png"
                    loading="lazy"
                    alt="Box 6. Court or Entity"
                    width={338}
                    height={42}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the name of the court or relevant entity that issued the
                  order or approved the agreement.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7. Case Number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-f-case-number-img.webp" />
                  <source srcSet="/assets/images/f1098-f-case-number-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-f-case-number-img.png"
                    loading="lazy"
                    alt="Box 7. Case Number"
                    width={432}
                    height={54}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the case number which is in court order or agreement.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8. Case Name or Names of Parties to Suit, Order, or
                  Agreement
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-f-case-name-img.webp" />
                  <source srcSet="/assets/images/f1098-f-case-name-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-f-case-name-img.png"
                    loading="lazy"
                    alt="Box 8. Case Name or Names of Parties to Suit, Order, or Agreement"
                    width={432}
                    height={54}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the case name or name of the parties to suit, order or
                  agreement.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9: Code
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-f-code-img.webp" />
                  <source srcSet="/assets/images/f1098-f-code-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-f-code-img.png"
                    loading="lazy"
                    alt="Box 9: Code"
                    width={217}
                    height={52}
                    className="img-responsive"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter one or more of the following codes. read the code
                  description carefully and choose the correct ones:
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 18, md: 16, sm: 15, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Code A: Multiple Payments
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Use Code A if the payer has made (or) is required to make
                  multiple payments to fulfill the total amount specified in the
                  suit, order, or agreement.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 18, md: 16, sm: 15, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Code B: Multiple Payers
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Use Code B if multiple payers are making multiple payments
                  under the suit, order, or agreement.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 18, md: 16, sm: 15, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Code C: Multiple payees
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Use code C if there are multiple payees under the suit, order,
                  or agreement.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 18, md: 16, sm: 15, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Code D: Provision of services or provision of property
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Use Code D if the suit, order, or agreement requires the payer
                  to provide services or transfer property.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 18, md: 16, sm: 15, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Code E: Payment amount not identified
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Use Code E if the suit, order, or agreement does not specify
                  some or all of the amount required for the violation,
                  investigation, or inquiry, or if it identifies a payment for
                  restitution, remediation, or compliance but leaves the total
                  amount or payment for property/services unspecified.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided in this resource is based on
                        the IRS guidelines. For more details, please refer the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1098f.pdf"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          instruction of Form 1098-F
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          General instruction Information returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Effortlessly file Form 1098-F with TaxZerone!
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              Ensure accuracy, stay compliant, and get instant IRS notifications
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098finstructions`}
              >
                Start filing Form 1098-F Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1098FInstructions;
